import React, { useCallback, useEffect, useRef, useState } from 'react';
import Filter from '@/pages/dashboard/components/NewFilter';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import classNames from 'classnames';
import MetricsFilter from './MetricsFilter';
import CompareFilter from './CompareFilter';
import { useFilterData } from './hooks';
import { shallowEqual, useSelector } from 'umi';
import { FRAME_TYPE_MAP } from '../../_config/constant';
import './index.scss';

/**
 *
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

// 使用自定义筛选器，不经分筛选器
const inputList: any[] = [];

// daily筛选器继承逻辑
const BenchmarkFilter = (props: any) => {
  const {
    className,
    style,
    filterConfirm,
    filterReset,
    filterInit,
    buttonLogConfig,
    children,
    ...resetProps
  } = props;
  const filterRef = useRef<any>({ compareType: 'overall' });

  const [filterValue, setFilterValue] = useState<any>({
    compareType: 'overall',
  });

  const { dataFrame, gameCode } = useSelector((state: any) => {
    return {
      dataFrame: state.dashboard?.currentGame?.data_frame || 'mobile',
      gameCode: state.dashboard?.currentGame?.game_code,
    };
  }, shallowEqual);

  const apiDataFrame = FRAME_TYPE_MAP[dataFrame || 'pc'];

  const {
    loading,
    genreOptions,
    metricOptions,
    defaultGameValue,
    defaultGenreValue,
    defaultMetricsValue,
  } = useFilterData(apiDataFrame, gameCode);

  useEffect(() => {
    setFilterValue({
      ...filterValue,
      metrics: { metrics: defaultMetricsValue },
      genre: defaultGenreValue,
    });
  }, [defaultGameValue, defaultGenreValue, defaultMetricsValue]);

  /**
   * 格式化过滤器的值。
   */
  const formatFilter = (value?: any) => {
    const {
      metrics,
      genre,
      compareType,
      gamesInfo = {},
    } = value || filterValue || {};
    const obj = {
      game_code: gameCode,
      frame_type: apiDataFrame,
      metrics: metrics?.metrics,
      compareType,
      genre_list: [],
      game_list: [],
      selector: { metricOptions, genreOptions },
      gamesInfo,
      changeGameCallback: changeGame,
    };
    if (compareType === 'genre') {
      obj.genre_list = [genre];
    }
    // 有操作过的，则按照操作过的游戏
    if (gamesInfo?.[compareType]) {
      const gameList = gamesInfo[compareType]?.map((item) => item.game_id);
      obj.game_list = gameList || [];
    } else if (compareType === 'game') {
      obj.game_list = defaultGameValue;
    }
    return obj;
  };

  const onInit = useCallback(() => {
    const filterVal = formatFilter();
    filterRef.current = filterVal;
    filterInit?.(filterVal);
  }, [filterInit, filterValue, metricOptions, genreOptions]);

  const onConfirm = useCallback(
    (_: any, inputList: any, value: any) => {
      const filterVal = formatFilter(value);
      // 少于3个指标，或者大于10个指标
      if (filterVal.metrics?.length < 3 || filterVal.metrics?.length > 10)
        return;
      filterRef.current = filterVal;
      filterConfirm?.(filterVal);
      setBtnPointLog(buttonLogConfig, filterVal);
    },
    [filterConfirm, filterValue, metricOptions, genreOptions],
  );

  const onReset = useCallback(() => {
    const defaultFilterValue = {
      compareType: 'overall',
      metrics: { metrics: defaultMetricsValue },
      genre: defaultGenreValue,
      gamesInfo: filterValue?.gamesInfo,
    };
    setFilterValue(defaultFilterValue);
    const filterVal = formatFilter(defaultFilterValue);
    filterRef.current = filterVal;
    filterConfirm?.(filterVal);
    setBtnPointLog(buttonLogConfig, filterVal, true);
  }, [filterReset, filterValue, metricOptions, genreOptions]);

  const changeMetrics = (value: any) => {
    setFilterValue({ ...filterValue, metrics: value });
  };

  const changeCompare = (value: string) => {
    setFilterValue({ ...filterValue, compareType: value });
  };

  const changeGenre = (value: string) => {
    setFilterValue({ ...filterValue, genre: value });
  };

  const changeGame = (key: string, value: any, isRefresh: boolean) => {
    const games = filterValue?.gamesInfo;
    let gamesInfo = {};
    if (key === 'game') {
      gamesInfo = { ...games, game: value };
    } else {
      // 除game外，其他选项共享游戏
      gamesInfo = { ...games, overall: value, genre: value };
    }
    setFilterValue({ ...filterValue, gamesInfo });
    if(isRefresh){
      onConfirm(null, null, { ...filterValue, gamesInfo });
    }
  };

  return (
    <div
      className={classNames(className, 'dbd-benchmark-filter-contaniner')}
      style={style}
    >
      <Filter
        {...resetProps}
        value={filterRef.current}
        inputList={inputList}
        loadingHeight={159}
        isHaveFilterCache={false}
        isHaveFilterSave={false}
        extraRenderContent={
          <div className="dbd-benchmark-filter">
            <MetricsFilter
              value={filterValue?.metrics}
              onChange={changeMetrics}
              options={metricOptions}
            />
            <CompareFilter
              value={filterValue?.compareType}
              genreValue={filterValue?.genre}
              onChange={changeCompare}
              genreOptions={genreOptions}
              changeGenre={changeGenre}
            />
          </div>
        }
        onInit={onInit}
        onConfirm={onConfirm}
        onReset={onReset}
        loading={loading}
        useOutInput
        confirmType={filterValue?.metrics?.metrics?.length < 3 ? 'disabled' : ''}
      >
        {React.Children.map(children, (child) => React.cloneElement(child, {}))}
      </Filter>
    </div>
  );
};

export default BenchmarkFilter;
